import React, { useCallback, useRef } from 'react';

/**
 * 01 · TRIM — which 30 seconds (handoff v3 §4).
 *
 * A Portal clip is 30 seconds at most, so a longer video has to give some up before it can go
 * anywhere. The step is never skipped, even for a short clip: trimming is also how you cut the
 * dead air off the front, which is most of what people actually want it for.
 *
 * Nothing about this leaves the device. The strip is frames drawn from the local file, and the
 * two numbers it produces travel with the Portal so every player loops inside them.
 *
 * Interaction: drag an end to trim, drag the middle to slide the window at its current length,
 * tap outside to pull the nearest end over. The window can never be wider than the cap or
 * narrower than a second, so there is always a valid clip and NEXT always works.
 */
/** The default. The live number comes from the server (`portal_clip_max_seconds`), so an admin
 *  can raise it without a deploy and the composer and the check stay in step. */
export const MAX_CLIP_MS = 30_000;
const MIN_CLIP_MS = 1_000;
/** The white handles, drawn just OUTSIDE the window on either side. */
const HANDLE_PX = 14;
/** Extra reach past a handle's outer side, for fingers. Never reaches into the window. */
const HANDLE_SLOP_PX = 10;

/**
 * What a press at `x` (px from the strip's left) grabs. In pixels, not time: a 30-second window
 * on a four-minute video is only ~90px wide, and a tolerance measured around its edges used to
 * swallow most of it, so pressing the middle trimmed an end instead of sliding. The handles sit
 * outside the window, so the window itself is always the slider and only the handles trim.
 */
export function grabAt(x, startPx, endPx) {
  if (x >= startPx - HANDLE_PX - HANDLE_SLOP_PX && x <= startPx) return 'start';
  if (x >= endPx && x <= endPx + HANDLE_PX + HANDLE_SLOP_PX) return 'end';
  if (x > startPx && x < endPx) return 'move';
  return x < startPx ? 'start' : 'end';   // tap outside: pull the nearer end over
}

export function fmtClip(sec) {
  const t = Math.max(0, Math.floor(sec || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
}

/** The frames (eight; more for a long video message), in a filled strip. Shared with the cover step. */
export function FrameStrip({ frames, children, stripRef = null, style, ...rest }) {
  const strip = frames?.length ? frames : new Array(8).fill(null);
  const last = strip.length - 1;
  return (
    <div ref={stripRef} style={{ position: 'relative', height: 64, borderRadius: 10, display: 'grid', gridTemplateColumns: `repeat(${strip.length}, minmax(0,1fr))`, touchAction: 'none', background: '#111', ...style }} {...rest}>
      {strip.map((src, i) => (
        <span key={i} style={{ display: 'block', minWidth: 0, background: '#222', overflow: 'hidden', borderRadius: i === 0 ? '10px 0 0 10px' : i === last ? '0 10px 10px 0' : 0 }}>
          {src && <img src={src} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }} />}
        </span>
      ))}
      {children}
    </div>
  );
}

export default function StepTrim({
  frames,
  durationSec,
  trim,                 // { startMs, endMs }
  onTrim,
  maxClipMs = MAX_CLIP_MS,
  playSec = 0,
  loading = false,
  failed = false,
}) {
  const stripRef = useRef(null);
  const dragging = useRef(null);

  const durationMs = Math.round((durationSec || 0) * 1000);
  const clipMs = Math.max(0, trim.endMs - trim.startMs);
  const atLimit = clipMs >= maxClipMs - 50;
  const overLimit = durationMs > maxClipMs + 50;

  const pct = (ms) => (durationMs ? (ms / durationMs) * 100 : 0);

  const onPointerDown = useCallback((e) => {
    const el = stripRef.current;
    if (!el || !durationMs) return;
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = el.getBoundingClientRect();
    const toMs = (x) => ((x - rect.left) / rect.width) * durationMs;
    const toPx = (ms) => (ms / durationMs) * rect.width;
    const at = toMs(e.clientX);

    const mode = grabAt(e.clientX - rect.left, toPx(trim.startMs), toPx(trim.endMs));
    const grabOffset = mode === 'move' ? at - trim.startMs : 0;

    const minClip = Math.min(MIN_CLIP_MS, durationMs);
    let latest = trim;

    const apply = (clientX) => {
      const t = Math.max(0, Math.min(durationMs, toMs(clientX)));
      let { startMs, endMs } = latest;
      let playhead;
      if (mode === 'start') {
        startMs = Math.max(0, endMs - maxClipMs, Math.min(t, endMs - minClip));
        playhead = startMs;
      } else if (mode === 'end') {
        endMs = Math.min(durationMs, startMs + maxClipMs, Math.max(t, startMs + minClip));
        playhead = endMs;
      } else {
        const len = endMs - startMs;
        startMs = Math.max(0, Math.min(durationMs - len, t - grabOffset));
        endMs = startMs + len;
        playhead = startMs;
      }
      latest = { startMs, endMs };
      onTrim(latest, { scrubToMs: playhead, settled: false });
    };

    dragging.current = true;
    if (mode !== 'move') apply(e.clientX);

    const onMove = (ev) => apply(ev.clientX);
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      dragging.current = false;
      // Back to the top of the clip and playing — and the cover follows the new window.
      onTrim(latest, { scrubToMs: latest.startMs, settled: true });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [durationMs, trim, onTrim, maxClipMs]);

  // Say what a press would do before it happens: resize arrows on a handle, a hand on the window.
  const onHover = useCallback((e) => {
    const el = stripRef.current;
    if (!el || !durationMs || dragging.current) return;
    const rect = el.getBoundingClientRect();
    const toPx = (ms) => (ms / durationMs) * rect.width;
    const mode = grabAt(e.clientX - rect.left, toPx(trim.startMs), toPx(trim.endMs));
    e.currentTarget.style.cursor = mode === 'move' ? 'grab' : 'ew-resize';
  }, [durationMs, trim]);

  const maxFmt = fmtClip(maxClipMs / 1000);
  const handle = (key, ms, side) => (
    <span
      key={key}
      aria-label={side === 'left' ? 'Trim start' : 'Trim end'}
      style={{
        position: 'absolute', top: -3, bottom: -3, width: HANDLE_PX,
        left: side === 'left' ? `calc(${pct(ms)}% - ${HANDLE_PX}px)` : `${pct(ms)}%`,
        background: '#fff', borderRadius: side === 'left' ? '8px 0 0 8px' : '0 8px 8px 0',
        display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
      }}
    >
      <span style={{ width: 2, height: 18, background: '#000' }} />
    </span>
  );

  return (
    <div className="font-mono text-white" style={{ display: 'grid', gap: 12 }} data-testid="step-trim">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em' }}>
        <span>WHICH {maxFmt.replace(/^0:/, '')} SECONDS</span>
        <span style={{ color: '#00FFFF' }}>{loading || failed ? 'LOADING…' : `${fmtClip(trim.startMs / 1000)} – ${fmtClip(trim.endMs / 1000)}`}</span>
      </div>

      {overLimit && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '10px 14px', background: 'rgba(255,255,255,.1)', borderRadius: 18 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em' }}>LONGER THAN {maxFmt}</span>
          <span style={{ fontSize: 11, lineHeight: 1.45, color: 'rgba(255,255,255,.75)' }}>
            This video is {fmtClip(durationMs / 1000)}. A Portal can be {maxFmt} at most, so pick the part to keep.
          </span>
        </div>
      )}

      {/* The wrapper's side padding is what lets the handles overhang the strip. */}
      <div style={{ padding: '0 14px', touchAction: 'none' }} onPointerDown={loading || failed ? undefined : onPointerDown} onPointerMove={loading || failed ? undefined : onHover}>
        {loading || failed ? (
          <div style={{ height: 64, borderRadius: 10, background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: failed ? '#EF4444' : 'rgba(255,255,255,.65)' }}>
            {failed ? "Couldn't read this video. Pick it again." : 'Loading video…'}
          </div>
        ) : (
          <FrameStrip frames={frames} stripRef={stripRef} data-testid="trim-strip" style={{ overflow: 'visible', cursor: 'grab' }}>
            <div aria-hidden style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${pct(trim.startMs)}%`, background: 'rgba(0,0,0,.72)', borderRadius: '10px 0 0 10px', pointerEvents: 'none' }} />
            <div aria-hidden style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: `${100 - pct(trim.endMs)}%`, background: 'rgba(0,0,0,.72)', borderRadius: '0 10px 10px 0', pointerEvents: 'none' }} />
            <div aria-hidden style={{ position: 'absolute', top: 0, bottom: 0, left: `${pct(playSec * 1000)}%`, width: 2, background: '#00FFFF', pointerEvents: 'none' }} />
            <div aria-hidden style={{ position: 'absolute', top: 0, bottom: 0, left: `${pct(trim.startMs)}%`, width: `${pct(clipMs)}%`, boxSizing: 'border-box', borderTop: '3px solid #fff', borderBottom: '3px solid #fff', pointerEvents: 'none' }} />
            {handle('start', trim.startMs, 'left')}
            {handle('end', trim.endMs, 'right')}
          </FrameStrip>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em' }}>
        <span style={{ color: 'rgba(255,255,255,.55)' }}>DRAG THE ENDS TO TRIM · THE MIDDLE TO SLIDE</span>
        <span style={{ flexShrink: 0, color: atLimit ? '#00FFFF' : '#fff' }}>{fmtClip(clipMs / 1000)} / {maxFmt} MAX</span>
      </div>
    </div>
  );
}
